import React, { useEffect, useState } from "react";
import classnames from "classnames";

import { Maximize16, Minimize16 } from "@carbon/icons-react";

import { Address } from "@eave/shared/components/Address";
import {
  AppShape,
  humanizedCashCloseTypes,
  humanizeLoanTypeName,
  operatorHumanizeLoanProgramName,
} from "@eave/shared/types/api";
import { formatCeilDecimal } from "@eave/shared/utils/percent";

import AppSlimDetails from "underwriter_dashboard/shared/AppSlimDetails";
import Money from "web_modules/Money";
import { loanDecisionToStr } from "web_modules/shared/loan_decisions";
import { loanStatusToStr } from "web_modules/shared/loan_statuses";
import { salesAppURL } from "underwriter_dashboard/shared";
import APIClient from "../../../APIClient";

const humanize = require("underscore.string/humanize");
const { FormattedDate } = require("react-intl");

const styles = require("./styles.module.scss");

// TODO: this should go somewhere else or styles need to be refactored
require("../../../../assets/stylesheets/components/underwriter_dashboard/shared/hurdle-chrome.scss");

const decimalToTwoPlaces = (d: string): string => (Number(d) * 100).toFixed(2);

const dash = <>&mdash;</>;
const notApplicable = "n/a";

interface AppSummaryHeaderProps {
  app: AppShape;
  navExpanded: boolean;
}

const AppSummaryHeader: React.FunctionComponent<AppSummaryHeaderProps> = ({ app, navExpanded }) => {
  const [minimized, setMinimized] = useState(false);
  const [mortgageLeadID, setMortgageLeadID] = useState<number>(0);

  const {
    application_status,
    borrowers,
    cash_close_type,
    computed_properties: {
      closing_date,
      loan_amount_base,
      loan_amount_total,
      loan_number,
      loan_type_name,
      ltv,
      purchased_property_address,
      purchased_property_county,
      purchase_price,
    },
    contacts,
    decision: { dti, dti_front },
    has_borrower2,
    investor_detail,
    operator_subject_property_type,
    property_county,
    property_type,
    property_usage,
    selected_loan_program,
    underwriting_summary,
    restricted,
    loan_purpose,
  } = app;

  useEffect(() => {
    APIClient.get(`/uw/loan_applications/${app.id}/cash_close`).then((resp: any) => {
      setMortgageLeadID(resp.cash_close_summary.lead_number);
    });
  }, [app.id]);

  let loanStatus = dash;
  if (underwriting_summary && underwriting_summary.loan_status) {
    const status = loanStatusToStr(underwriting_summary.loan_status);
    loanStatus = status.replace(" (SET BY SYSTEM)", ""); // trim for this display context
  }

  let loanDecision = dash;
  if (underwriting_summary && underwriting_summary.loan_decision) {
    loanDecision = loanDecisionToStr(underwriting_summary.loan_decision);
  }

  let loanPurpose = "Purchase";
  if (loan_purpose === "Bridge") {
    loanPurpose = "Bridge";
  }

  let applicationStatus: string;
  switch (application_status) {
    case "prospect":
      applicationStatus = "Prospect";
      break;
    case "pre_app":
      applicationStatus = "Pre-app";
      break;
    case "respa_complete":
      applicationStatus = "RESPA-complete";
      break;
    default:
      applicationStatus = "???";
      break;
  }

  if (purchased_property_address !== undefined) {
    purchased_property_address.zip = purchased_property_address.postal_code.split("-")[0];
  }

  const propertyType = operator_subject_property_type || property_type;

  const headerStyles = classnames({
    [styles.header]: true,
    [styles.minimized]: minimized,
    [styles.employeeHeader]: app.restricted,
  });

  return (
    <header className={headerStyles}>
      <div className={styles.minimizeMaximize} onClick={() => setMinimized(!minimized)}>
        {minimized && <Maximize16 />}
        {!minimized && <Minimize16 />}
      </div>

      <AppSlimDetails app={app} isVisible={minimized} navExpanded={navExpanded} />
      <div
        className={classnames("grid-container", styles.headerContainer, minimized && styles.hidden)}
      >
        <div className={`grid-x grid-padding-x ${styles.gridContainer}`}>
          <div className={classnames("cell", "medium-3", styles.appSummaryColumn)}>
            <ul>
              <li>
                <span>Loan status</span>
                <strong>{loanStatus}</strong>
              </li>
              <li>
                <span>Loan decision</span>
                <strong>{loanDecision}</strong>
              </li>
              <li>
                <span>Application status</span>
                <strong>{applicationStatus}</strong>
              </li>
              <li>
                <span>Cash Close</span>
                <strong>{humanizedCashCloseTypes[cash_close_type]}</strong>
              </li>
              <li>
                <span>Loan purpose</span>
                <strong>{loanPurpose}</strong>
              </li>
              <li>
                <span>Loan #</span>
                <strong>{loan_number || dash}</strong>
              </li>
              <li>
                <span>Lead #</span>
                <strong>
                  {mortgageLeadID > 0 ? (
                    <a
                      rel="noreferrer"
                      target="_blank"
                      href={`${salesAppURL()}/mortgages/${mortgageLeadID}`}
                    >
                      {mortgageLeadID}
                    </a>
                  ) : (
                    dash
                  )}
                </strong>
              </li>
            </ul>
          </div>
          <div className={classnames("cell", "auto", styles.flexColumn)}>
            <ul>
              <li>
                <span>Borrower 1</span>
                <strong>
                  {borrowers[1]
                    ? `${borrowers[1].first_name} ${borrowers[1].last_name}`
                    : notApplicable}
                </strong>
              </li>
              <li>
                <span>Borrower 2</span>
                <strong>
                  {borrowers[2]
                    ? `${borrowers[2].first_name} ${borrowers[2].last_name}`
                    : notApplicable}
                </strong>
              </li>
              <li>
                <span>Borrower 3</span>
                <strong>
                  {borrowers[4]
                    ? `${borrowers[4].first_name} ${borrowers[4].last_name}`
                    : notApplicable}
                </strong>
              </li>
              <li>
                <span>Borrower 4</span>
                <strong>
                  {borrowers[8]
                    ? `${borrowers[8].first_name} ${borrowers[8].last_name}`
                    : notApplicable}
                </strong>
              </li>
              <li>
                <span>Property address</span>
                <strong>
                  {purchased_property_address && purchased_property_address.line1 ? (
                    <Address address={purchased_property_address} format="multi" />
                  ) : (
                    dash
                  )}
                </strong>
              </li>
              <li>
                <span>County</span>
                <strong>{purchased_property_county || property_county}</strong>
              </li>
            </ul>
          </div>
          <div className={classnames("cell", "medium-3", styles.appSummaryColumn)}>
            <ul>
              <li>
                <span>Occupancy</span>
                <strong>{humanize(property_usage)}</strong>
              </li>
              <li>
                <span>Property type</span>
                <strong>{propertyType}</strong>
              </li>
              <li>
                <span>Purchase price</span>
                <strong>
                  <Money value={purchase_price} />
                </strong>
              </li>
              <li>
                <span>Appraisal value</span>
                <strong>
                  {app.appraisal && app.appraisal.appraised_value !== "0" ? (
                    <Money value={app.appraisal.appraised_value} />
                  ) : (
                    dash
                  )}
                </strong>
              </li>
              <li>
                <span>Base loan amount</span>
                <strong>
                  <Money value={loan_amount_base} />
                </strong>
              </li>
              <li>
                <span>Total loan amount</span>
                <strong>
                  <Money value={loan_amount_total} />
                </strong>
              </li>
              <li>
                <span>LTV</span>
                <strong>{ltv ? formatCeilDecimal(parseFloat(ltv) * 100, 2) : dash}</strong>
              </li>
              <li>
                <span>DTI ratios</span>
                <strong>
                  {decimalToTwoPlaces(dti_front)} / {decimalToTwoPlaces(dti)}
                </strong>
              </li>
            </ul>
          </div>
          <div className={classnames("cell", "medium-3", styles.appSummaryColumn)}>
            <ul className={styles.investorColumn}>
              <li>
                <span>Investor</span>
                <strong>{(investor_detail && investor_detail.investor_name) || dash}</strong>
              </li>
              <li>
                <span>Loan officer</span>
                <strong>
                  {contacts.loan_officer ? contacts.loan_officer.name : notApplicable}
                </strong>
              </li>
              <li>
                <span>Underwriter</span>
                <strong>
                  {contacts.loan_underwriter ? contacts.loan_underwriter.name : notApplicable}
                </strong>
              </li>
              <li>
                <span>Loan manager</span>
                <strong>
                  {contacts.loan_manager ? contacts.loan_manager.name : notApplicable}
                </strong>
              </li>
              <li>
                <span>Closer/funder</span>
                <strong>{contacts.closer_funder?.name || notApplicable}</strong>
              </li>
              <li>
                <span>Product</span>
                <strong>{operatorHumanizeLoanProgramName[selected_loan_program]}</strong>
              </li>
              <li>
                <span>Loan program</span>
                <strong>{humanizeLoanTypeName[loan_type_name]}</strong>
              </li>
              <li>
                <span>COE date</span>
                <strong>
                  {closing_date ? (
                    <FormattedDate value={closing_date} timeZone="UTC" />
                  ) : (
                    notApplicable
                  )}
                </strong>
              </li>
            </ul>
          </div>
          {restricted && (
            <div style={{ fontSize: "32px", float: "right" }} title="Employee Loan">
              <i className="fa fa-id-badge" />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppSummaryHeader;
